import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CalendarDays, ChevronLeft, ChevronRight, Plus, Trash2, Lock } from "lucide-react";
import { AddEventDialog } from "@/components/app/AddEventDialog";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monday = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

export default function CalendarPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState(user?.id);
  const [week, setWeek] = useState(monday(new Date()));
  const [events, setEvents] = useState([]);
  const [dialogDate, setDialogDate] = useState(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(week, i)), [week]);
  const member = members.find((m) => m.id === memberId);
  const today = iso(new Date());

  useEffect(() => { api.get("/calendar/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);
  const load = useCallback(() => {
    if (!memberId) return;
    api.get("/calendar/events", { params: { user_id: memberId, date_from: iso(days[0]), date_to: iso(days[6]) } })
      .then((r) => setEvents(r.data)).catch(() => {});
  }, [memberId, days]);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este evento?")) return;
    await api.delete(`/calendar/events/${id}`).catch(() => {});
    setEvents((e) => e.filter((x) => x.id !== id));
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-[1500px]" data-testid="calendar-page">
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Equipo</div>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1 mb-6 flex items-center gap-3">
        <CalendarDays className="text-[#E63946]" size={30} /> Calendario
      </h1>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <select value={memberId || ""} onChange={(e) => setMemberId(e.target.value)} className="h-11 border border-black px-3 bg-white text-sm md:w-80" data-testid="calendar-member">
          {members.map((m) => <option key={m.id} value={m.id}>{m.id === user?.id ? `Mi calendario (${m.name})` : m.name}</option>)}
        </select>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeek(addDays(week, -7))} className="h-11 w-11 border border-black flex items-center justify-center hover:bg-zinc-100" data-testid="calendar-prev" aria-label="Semana anterior"><ChevronLeft size={16} /></button>
          <button onClick={() => setWeek(monday(new Date()))} className="h-11 px-4 border border-black text-xs font-semibold uppercase tracking-widest hover:bg-zinc-100" data-testid="calendar-today">Hoy</button>
          <button onClick={() => setWeek(addDays(week, 7))} className="h-11 w-11 border border-black flex items-center justify-center hover:bg-zinc-100" data-testid="calendar-next" aria-label="Semana siguiente"><ChevronRight size={16} /></button>
          <span className="text-sm font-semibold ml-2" data-testid="calendar-range">
            {days[0].toLocaleDateString("es-ES", { day: "numeric", month: "short" })} – {days[6].toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
        {days.map((d) => {
          const key = iso(d);
          const dayEvents = events.filter((e) => e.date === key);
          return (
            <div key={key} className={`border-2 bg-white min-h-[180px] flex flex-col ${key === today ? "border-[#E63946]" : "border-black"}`} data-testid={`calendar-day-${key}`}>
              <div className="px-3 py-2 border-b border-black flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{d.toLocaleDateString("es-ES", { weekday: "long" })}</div>
                  <div className="font-display font-bold">{d.getDate()}</div>
                </div>
                <button onClick={() => setDialogDate(key)} className="w-8 h-8 border border-black flex items-center justify-center hover:bg-black hover:text-white transition-colors" data-testid={`calendar-add-${key}`} aria-label="Añadir">
                  <Plus size={14} />
                </button>
              </div>
              <div className="p-2 space-y-2 flex-1">
                {dayEvents.length === 0 && <div className="text-[11px] text-zinc-400 px-1">Libre</div>}
                {dayEvents.map((e) => (
                  <div key={e.id} className={`p-2 text-xs border-l-4 ${e.private ? "bg-zinc-100 border-zinc-400" : "bg-[#FFF8E1] border-[#F5B301]"}`} data-testid={`calendar-event-${e.id}`}>
                    <div className="flex items-start justify-between gap-1">
                      <div className="font-mono font-semibold">{e.start}–{e.end}</div>
                      {!e.private && (
                        <button onClick={() => remove(e.id)} className="text-zinc-500 hover:text-[#E63946]" data-testid={`calendar-delete-${e.id}`} aria-label="Eliminar"><Trash2 size={12} /></button>
                      )}
                    </div>
                    <div className="font-semibold mt-0.5 flex items-center gap-1">{e.private && <Lock size={11} />} {e.title}</div>
                    {e.notes && <div className="text-zinc-600 mt-0.5">{e.notes}</div>}
                    {!e.private && e.created_by !== e.owner_id && <div className="text-[10px] text-zinc-500 mt-1">Puesto por {e.created_by_name}</div>}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {member && (
        <AddEventDialog key={`${memberId}-${dialogDate}`} open={!!dialogDate} onOpenChange={(v) => !v && setDialogDate(null)} member={member} defaultDate={dialogDate}
          onCreated={(ev) => { setDialogDate(null); setEvents((x) => [...x, ev]); }} />
      )}
    </div>
  );
}
